/**
 * Email Notification Service
 * Sends automated email notifications via Supabase Edge Function + Resend
 */

import { supabase } from '@/lib/supabase';

interface NotificationResponse {
  success: boolean;
  error?: string;
}

/**
 * Send a notification email via Supabase Edge Function
 */
async function sendNotification(
  type: 'new_lead' | 'task_assigned' | 'new_feedback' | 'new_feature_suggestion' | 'feedback_resolved',
  to: string[],
  data: Record<string, any>
): Promise<NotificationResponse> {
  try {
    const { data: result, error } = await supabase.functions.invoke('send-notification', {
      body: { type, to, data },
    });

    if (error) {
      console.error('[sendNotification] Error:', error);
      return { success: false, error: error.message };
    }

    return result as NotificationResponse;
  } catch (error: any) {
    console.error('[sendNotification] Error:', error);
    return { success: false, error: error.message };
  }
}

// =============================================================================
// EVENT-BASED NOTIFICATIONS
// =============================================================================

/**
 * Send notification when a task is assigned to someone
 */
export async function notifyTaskAssigned(
  task: {
    id: string;
    title: string;
    description?: string;
    due_date?: string;
    priority: string;
    deal_name?: string;
    property_name?: string;
    assigned_by_name?: string;
  },
  assigneeEmail: string
): Promise<NotificationResponse> {
  return sendNotification('task_assigned', [assigneeEmail], task);
}

/**
 * Send notification when new feedback is submitted
 */
export async function notifyNewFeedback(
  feedback: {
    id: string;
    feedback_type: string;
    description: string;
    page_url: string;
    page_title?: string;
    user_name?: string;
    user_email?: string;
    has_screenshot: boolean;
  },
  recipients: string[]
): Promise<NotificationResponse> {
  if (recipients.length === 0) {
    console.warn('[notifyNewFeedback] No recipients provided');
    return { success: false, error: 'No recipients' };
  }

  return sendNotification('new_feedback', recipients, feedback);
}

/**
 * Send notification when a new feature suggestion is submitted
 */
export async function notifyNewFeatureSuggestion(
  suggestion: {
    id: string;
    title: string;
    description: string;
    category: string;
    priority: string;
    user_name?: string;
    user_email?: string;
  },
  recipients: string[]
): Promise<NotificationResponse> {
  if (recipients.length === 0) {
    console.warn('[notifyNewFeatureSuggestion] No recipients provided');
    return { success: false, error: 'No recipients' };
  }

  return sendNotification('new_feature_suggestion', recipients, suggestion);
}

/**
 * Send notification to the submitter when their feedback is marked resolved
 */
export async function notifyFeedbackResolved(feedback: {
  id: string;
  feedback_type: string;
  description: string;
  page_title?: string;
  page_url?: string;
  user_name?: string;
  user_email?: string;
  admin_notes?: string;
  resolved_at: string;
}): Promise<NotificationResponse> {
  if (!feedback.user_email) {
    console.warn('[notifyFeedbackResolved] No submitter email — skipping notification');
    return { success: false, error: 'No submitter email' };
  }

  return sendNotification('feedback_resolved', [feedback.user_email], feedback);
}

/**
 * Check if email notifications are enabled
 * Returns true - Resend is configured via Supabase secrets
 */
export function isEmailNotificationsEnabled(): boolean {
  return true;
}
